import { formatDate } from '@angular/common';

import { isEmptyValue } from '../../utils/empty-value.util';
import { resolveDocumentFileName } from '../../utils/document-file-meta';
import { getCompositeDisplayValue } from './composite-subfields.util';
import { resolveManualOptionLabel, resolveManualOptionValue } from './manual-option.util';
import type { ManualOptionSource } from './manual-option.types';
import type { Entry } from '../../../features/admin/modules/modules.model';
import type { ReviewValueContext } from './review-value.types';

export type { ReviewValueContext } from './review-value.types';

/**
 * SHARED value→display-string core for the two answer-review surfaces: the wizard's own
 * "Review answers" step (wizard-review.component) and the admin's onboarding-review card
 * (readonly-onboarding.component). One implementation, so display rules (Ja/Nee, dates,
 * composite shapes, masking) cannot drift between what the applicant confirms and what the
 * reviewer approves. `UtilityService.valueToInterfaceValue` stays separate on purpose — it has
 * table semantics ('<br>' joins, column addons) that are wrong for review rows.
 *
 * Each surface keeps its own WALKER (which fields become rows: the wizard reads live
 * formControl values and shows missing/optional badges; the review card walks the persisted
 * generatedForm against the entry with conditional-hide evaluation) — only the formatting of a
 * single value is shared.
 */

/**
 * Sub-keys that are storage/technical detail rather than something a user recognises in a
 * summary: geodata (a geocoded address carries lat/long and a geofence radius), reference
 * plumbing (ids, slugs) and Cosmos/audit metadata. Excluded from the generic "join the
 * object's leaf values" fallback so an address does not read as a wall of numbers and an
 * envelope never leaks etags or a colleague's e-mail into an answer row.
 */
export const TECHNICAL_SUBKEYS = new Set([
  'latitude',
  'longitude',
  'geoRadius',
  'id',
  'moduleId',
  'ModuleId',
  'documentType',
  'slug',
  'partitionKey',
  '_etag',
  '_rid',
  '_self',
  '_ts',
  '_attachments',
  'createdAt',
  'createdByEmail',
  'createdByUserId',
  'updatedAt',
  'updatedByEmail',
  'updatedByUserId',
]);

/**
 * Layout-containers: no value of their own, but their child fields are walked. A LABELED
 * field with a fieldGroup that is NOT in this set is a composite leaf (name, address,
 * date_range, geolocation, …) — one row, never one per sub-key.
 */
export const CONTAINER_TYPES = new Set([
  'group',
  'tabs',
  'pages',
  'wizard',
  'accordion',
  undefined as unknown as string,
]);

/**
 * Field types whose whole point is that the value stays hidden. A review row confirms that
 * they are filled in, never what they contain — printing them would undo the masking.
 */
const MASKED_TYPES = new Set(['password', 'custom-password', 'secure-input']);

/** Placeholder shown instead of a masked field's value. */
export const MASKED_PLACEHOLDER = '••••••••';

/**
 * Human-readable display of one answered value; `null` = nothing to show (empty / technical
 * only). Deliberately conservative on objects: an object without a recognizable display shape
 * is dropped rather than JSON-dumped, so a review never shows raw envelopes.
 */
export function formatReviewValue(value: unknown, context: ReviewValueContext = {}): string | null {
  const core = formatCoreValue(value, context);
  // Masked types confirm "filled in" without disclosing the secret — owned HERE so every
  // (future) caller of the shared entry point inherits the masking instead of owing an extra
  // step. The core decides FIRST whether there is anything to show: a value the formatter
  // itself considers empty (whitespace-only, unrenderable) keeps the missing-answer badge
  // instead of suddenly reading as filled in because the field type is secret.
  if (core !== null && context.type && MASKED_TYPES.has(context.type)) {
    return MASKED_PLACEHOLDER;
  }
  return core;
}

function formatCoreValue(value: unknown, context: ReviewValueContext): string | null {
  if (isEmptyValue(value)) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nee';
  }
  if (value instanceof Date) {
    // An Invalid Date (a datepicker writes one for unparseable typed input) makes formatDate
    // throw, which would take down the whole summary render — treat it as empty instead.
    return isNaN(value.getTime()) ? null : formatDate(value, 'dd-MM-yyyy', 'nl');
  }

  // Central composite registry first (rdw → plate, kvk → kvkNumber). An EMPTY primary falls
  // through instead of swallowing the answer: the RDW clear button in manual mode leaves
  // { plate: '', ...typed vehicle fields } — the generic handling below joins those fields,
  // where returning null here would hide everything the applicant typed.
  const composite = context.type ? getCompositeDisplayValue(context.type, value) : null;
  if (composite !== null && composite.trim() !== '') {
    return composite.trim();
  }

  if (Array.isArray(value)) {
    const parts = value
      .map(item => formatReviewValue(item, context))
      .filter((part): part is string => part !== null && part !== '');
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'object') {
    return formatObjectValue(value as Record<string, unknown>);
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }
  if (text === 'true' || text === 'false') {
    return text === 'true' ? 'Ja' : 'Nee';
  }
  const date = formatDateString(text);
  if (date !== null) {
    return date;
  }
  // Static option sets (radio-style questions): show the option label, not the stored value.
  const optionLabel = lookupOptionLabel(text, context);
  if (optionLabel !== null) {
    return optionLabel;
  }
  return text;
}

function formatObjectValue(obj: Record<string, unknown>): string | null {
  const checklist = formatChecklistValue(obj);
  if (checklist !== undefined) {
    return checklist;
  }

  // Entry-small selections (selectedInput), name groups and documents each carry their own
  // representative key; the part-assemblies below cover half-filled groups. The FIRST
  // NON-EMPTY candidate wins — envelopes routinely carry selectedInput: '' when the display
  // field was never resolved, and a `??` chain would short-circuit on that empty string.
  const candidate = firstNonEmptyString([
    obj['selectedInput'],
    obj['fullAddress'],
    obj['fullName'],
    obj['name'],
    obj['label'],
    obj['title'],
    obj['fileName'],
  ]);
  if (candidate) {
    return candidate;
  }

  // Name group without fullName: assemble from its parts.
  const nameJoin = joinStringParts([obj['firstName'], obj['affix'], obj['lastName']], ' ');
  if (nameJoin) {
    return nameJoin;
  }
  // Address group without fullAddress: street + number, postal code, city.
  const street = joinStringParts([obj['streetName'], obj['number']], ' ');
  const addressJoin = joinStringParts([street, obj['postalCode'], obj['city']], ', ');
  if (addressJoin) {
    return addressJoin;
  }

  // Uploaded-document reference (upload-document-entry stores the created document's full
  // envelope): show the concrete file, falling back to the record name. Envelopes without
  // either stay dropped — never generic-join arbitrary linked envelopes.
  const inner = obj['entry'];
  if (inner && typeof inner === 'object') {
    const fileName = resolveDocumentFileName(obj as unknown as Entry);
    if (fileName) {
      return fileName;
    }
    const recordName = (inner as Record<string, unknown>)['name'];
    return typeof recordName === 'string' && recordName.trim() ? recordName.trim() : null;
  }

  // Generic composite fallback (date_range {start,end}, repeat rows, …): join the
  // non-technical string/number leaves, each through the shared scalar formatting so joined
  // dates and booleans read like every other row on the card. Objects with only technical
  // leaves stay dropped.
  const parts = Object.entries(obj)
    .filter(([key]) => !TECHNICAL_SUBKEYS.has(key))
    .map(([, part]) => part)
    .filter((part): part is string | number => typeof part === 'string' || typeof part === 'number')
    .map(part => formatReviewValue(part) ?? '')
    .filter(part => part !== '');
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * The required-documents checklist writes a structured value
 * (`{ requiredDocuments, optionalDocuments, pendingRequiredDocumentsCount }`). The generic
 * object fallback keeps only string/number leaves, so it would print the bare pending count —
 * "1" — which reads as "one required document" instead of "one still missing". Summarise
 * upload progress instead. Returns `undefined` when the value is not a checklist, so the
 * caller falls through.
 */
function formatChecklistValue(obj: Record<string, unknown>): string | null | undefined {
  const required = obj['requiredDocuments'];
  const optional = obj['optionalDocuments'];
  if (!Array.isArray(required) || !Array.isArray(optional)) {
    return undefined;
  }
  const hasFile = (doc: unknown): boolean => (doc as { hasFile?: boolean })?.hasFile === true;
  const total = required.length;
  if (total === 0) {
    // No required templates for this onboarder — uploaded optionals are the only thing to report.
    const uploadedOptional = optional.filter(hasFile).length;
    return uploadedOptional > 0 ? `${uploadedOptional} optionele geüpload` : null;
  }
  const uploaded = required.filter(hasFile).length;
  const summary = `${uploaded} van ${total} geüpload`;
  const uploadedOptional = optional.filter(hasFile).length;
  return uploadedOptional > 0 ? `${summary} (+${uploadedOptional} optioneel)` : summary;
}

function formatDateString(text: string): string | null {
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    // A date-only string MUST be built from its parts. `new Date('2026-07-28')` is parsed as
    // UTC midnight, and formatDate then renders it in the viewer's zone — so everyone west of
    // UTC would read back the previous day and confirm a date they never entered.
    const parsed = localDate(+dateOnly[1], +dateOnly[2], +dateOnly[3]);
    return parsed ? formatDate(parsed, 'dd-MM-yyyy', 'nl') : text;
  }
  // Rule 1 — midnight on the SOURCE clock is a date in disguise: forms store dates as
  // 'yyyy-MM-ddT00:00:00' on plain input/time fields, and backend effects / Excel imports
  // serialize the same dates as UTC/offset midnights ('…T00:00:00Z', '…+00:00'). Deciding on
  // the SOURCE string keeps all of those a plain calendar date and preserves the day the
  // string names. Only literal zero seconds count: a midnight minute with real seconds is not
  // a stored calendar date, rule 2 below decides it.
  const midnight = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ]00:00(?::00(?:\.0+)?)?(Z|[+-]\d{2}:?\d{2})?$/);
  if (midnight) {
    const parsed = localDate(+midnight[1], +midnight[2], +midnight[3]);
    return parsed ? formatDate(parsed, 'dd-MM-yyyy', 'nl') : text;
  }
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(text)) {
    // A date-TIME string carries its own offset semantics; leave the parsing to Date.
    const parsed = new Date(text.replace(' ', 'T'));
    if (!isNaN(parsed.getTime())) {
      const formatted = formatDate(parsed, 'dd-MM-yyyy HH:mm', 'nl');
      // Rule 2 — the RENDERED clock: an instant that lands on local midnight is the zone
      // artifact of a stored calendar date (a UTC host re-serializes '+02:00' midnights as
      // '…T22:00:00Z' on any backend write-back), never a time a user chose — no writer here
      // stores a chosen time-of-day as a foreign-offset instant. A genuine local-midnight
      // source already matched rule 1, so this only strips the fabricated clock.
      return formatted.endsWith(' 00:00') ? formatted.slice(0, -' 00:00'.length) : formatted;
    }
  }
  return null;
}

/**
 * Local-midnight Date for a y/m/d triple, or null when the triple is not a real calendar date.
 * The round-trip check rejects rollovers: `new Date(2026, 12, 45)` silently becomes 2027-01-14,
 * which would render nonsense like "2026-13-45" as a plausible date instead of leaving it alone.
 */
function localDate(year: number, month: number, day: number): Date | null {
  const parsed = new Date(year, month - 1, day);
  const roundTrips = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
  return roundTrips ? parsed : null;
}

/**
 * Stored option value → display label. Prefers live `options`; falls back to the persisted
 * `optionsArray`'s manual sets, applying the same label/value fallbacks the form generator
 * uses at runtime (`resolveManualOptionLabel`/`resolveManualOptionValue`, shared with
 * `FormService.generateManualOptions` — truthy fallback: an empty-string `label`/`value` falls
 * back to `option`, same as what the live form actually renders); last, the caller's resolver
 * for store-backed sets (taxonomy / module) the persisted form cannot carry.
 */
function lookupOptionLabel(text: string, context: ReviewValueContext): string | null {
  const candidates =
    Array.isArray(context.options) && context.options.length > 0
      ? (context.options as Record<string, unknown>[])
      : manualOptionsOf(context.optionsArray);
  if (!candidates) {
    return context.resolveOptionLabel?.(text) ?? null;
  }
  for (const option of candidates) {
    if (!option || typeof option !== 'object') {
      continue;
    }
    const manualOption = option as ManualOptionSource;
    if (resolveManualOptionValue(manualOption) !== text) {
      continue;
    }
    const label = resolveManualOptionLabel(manualOption);
    if (typeof label === 'string' && label.trim()) {
      return label.trim();
    }
  }
  return context.resolveOptionLabel?.(text) ?? null;
}

function manualOptionsOf(optionsArray: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(optionsArray)) {
    return null;
  }
  const collected = optionsArray.flatMap(set =>
    (set as { optionsType?: string; options?: unknown })?.optionsType === 'manual' &&
    Array.isArray((set as { options?: unknown }).options)
      ? ((set as { options: Record<string, unknown>[] }).options ?? [])
      : []
  );
  return collected.length > 0 ? collected : null;
}

function firstNonEmptyString(candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate.trim();
    }
  }
  return null;
}

function joinStringParts(parts: unknown[], separator: string): string | null {
  const joined = parts
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .map(part => part.trim())
    .join(separator);
  return joined || null;
}
